/**
 * Redeeming an invite, including the half that was never wired up.
 *
 * `groups.join` creates the membership and hands back the group key sealed
 * under the invite code — deliberately, and the comment on that procedure says
 * why: the browser is meant to open it with the code it already has and re-seal
 * it to its own public key, because the server cannot. Nothing ever did.
 *
 * The consequence was quiet and total. Somebody accepted an invite, arrived in
 * the group, and every photograph in it was locked to them, permanently, with
 * no error to explain why — the membership was real, the grant was missing, and
 * only another member happening to open the group would ever create one. It
 * looked exactly like "the encryption is broken".
 *
 * Both ways in go through here so they cannot drift apart again.
 */

import { adoptGroupKeyFromInvite } from "../../lib/keyring.js";
import { trpcClient } from "../../lib/trpc.js";

export interface JoinResult {
  groupId: string;
  /**
   * Whether this browser now holds the key to the group.
   *
   * False is survivable and not worth blocking on: the membership exists, and
   * any member who opens the group can grant the key afterwards. It is worth
   * *reporting*, though, because it is the difference between "your photos are
   * loading" and "your photos will stay locked until somebody else opens this
   * group".
   */
  keyAdopted: boolean;
}

export async function joinGroupWithCode(code: string): Promise<JoinResult> {
  const result = await trpcClient.groups.join.mutate({ code });

  /*
   * The invite carries no key.
   *
   * Two very different situations, and from here they look the same: an old
   * group with no encryption at all, or — far more likely — an invite made
   * before the client started sealing the key into them. This used to report
   * success for both, so somebody would join, be told nothing, and find every
   * photograph locked with no explanation.
   *
   * Reported as not adopted. The group screen then knows to say what is
   * actually happening, and an unencrypted group never reaches that state
   * because it has nothing to be locked out of.
   */
  if (!result.invite?.wrappedGroupKey || !result.invite.keySalt) {
    return { groupId: result.groupId, keyAdopted: false };
  }

  try {
    const wrappedForMe = await adoptGroupKeyFromInvite(
      result.groupId,
      code,
      result.invite.wrappedGroupKey,
      result.invite.keySalt,
    );

    // Re-sealed to this account's public key, so it survives this browser.
    await trpcClient.groups.storeGroupKey.mutate({
      groupId: result.groupId,
      wrappedGroupKey: wrappedForMe,
    });

    return { groupId: result.groupId, keyAdopted: true };
  } catch {
    /*
     * Swallowed on purpose. The person is already a member by this point — the
     * server committed that before returning — so throwing here would report a
     * failed join that in fact succeeded, and a second attempt would then be
     * refused as "you are already in this group". The caller is told instead.
     */
    return { groupId: result.groupId, keyAdopted: false };
  }
}
